import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, MapPin, Pencil, Trash2, Users } from "lucide-react";

export default function Areas() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");

  const { data: areas = [], isLoading } = useQuery({ queryKey: ["areas-full"], queryFn: async () => (await api.get("/areas")).data });

  const save = useMutation({
    mutationFn: () => (editing ? api.put(`/areas/${editing}`, { name }) : api.post("/areas", { name })),
    onSuccess: () => {
      toast.success(editing ? "Area updated" : "Area added");
      qc.invalidateQueries({ queryKey: ["areas-full"] });
      qc.invalidateQueries({ queryKey: ["areas"] });
      setDialog(false); setName(""); setEditing(null);
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/areas/${id}`),
    onSuccess: () => { toast.success("Area removed"); qc.invalidateQueries({ queryKey: ["areas-full"] }); qc.invalidateQueries({ queryKey: ["areas"] }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  return (
    <div className="space-y-6" data-testid="areas-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Areas</h1>
          <p className="mt-1 text-sm text-muted-foreground">{areas.length} localities · used across customers, deliveries &amp; reports</p>
        </div>
        <Button onClick={() => { setEditing(null); setName(""); setDialog(true); }} data-testid="area-add-button">
          <Plus className="mr-2 h-4 w-4" /> Add Area
        </Button>
      </div>

      {isLoading ? (
        <div className="h-40 animate-pulse rounded-xl bg-secondary" />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {areas.map((a, i) => (
            <motion.div key={a.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
              <Card className="flex items-center justify-between p-5" data-testid={`area-card-${a.id}`}>
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary"><MapPin className="h-5 w-5" /></span>
                  <div>
                    <div className="font-semibold">{a.name}</div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground"><Users className="h-3 w-3" />{a.customers} customers</div>
                  </div>
                </div>
                <div className="flex gap-1">
                  {user?.role === "admin" && (
                    <>
                      <Button variant="ghost" size="icon" onClick={() => { setEditing(a.id); setName(a.name); setDialog(true); }} data-testid={`area-edit-${a.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => del.mutate(a.id)} data-testid={`area-delete-${a.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </>
                  )}
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent data-testid="area-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Area" : "Add Area"}</DialogTitle>
            <DialogDescription>These areas appear as the default options when adding customers.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Area name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Green Glen" data-testid="area-name-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
            <Button onClick={() => save.mutate()} disabled={!name.trim() || save.isPending} data-testid="area-save-button">{editing ? "Save" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
